import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { type ReactNode } from 'react'
import {
  CardItemContent,
  CardItemIcon,
  CardList,
  CardListItem,
  CardSubtitle,
  CardTitle,
} from '@/components/Card'
import { type IconName } from '@/components/Icon'
import { AutoSkeleton } from '@/components/Skeleton'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { Spinner } from '@/components/Spinner'

interface ServiceTypesDialogProps {
  isOpen: boolean
  children?: ReactNode
  isCloseDisabled?: boolean
  onClose: () => void
  testId?: string
  closeButtonTestId?: string
}

/**
 * The ServiceTypesDialog
 */
export function ServiceTypesDialog({
  children,
  isOpen,
  onClose,
  isCloseDisabled,
  testId,
  closeButtonTestId,
}: ServiceTypesDialogProps) {
  return (
    <ModalDialog
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={!isCloseDisabled}
      testId={testId}
    >
      <ModalDialogHeader>
        <ModalDialogTitle>What do you need help with?</ModalDialogTitle>
        <ModalDialogDescription>
          Select a service to get started. <br /> We'll handle the rest.
        </ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>
        <CardList aria-label="Service types">{children}</CardList>
      </ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button
            label="Close"
            variant="outline"
            onPress={onClose}
            isDisabled={isCloseDisabled}
            testId={closeButtonTestId}
          />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

interface ServiceTypeListItemProps {
  title: string
  description: string
  icon: IconName
  isSkeleton?: boolean
  isDisabled?: boolean
  isPending?: boolean
  onAction?: () => void
  testId?: string
}

/**
 * The ServiceTypeListItem
 */
export function ServiceTypeListItem({
  title,
  description,
  icon,
  isSkeleton,
  onAction,
  isDisabled,
  isPending,
  testId,
}: ServiceTypeListItemProps) {
  const { vary } = useSkeletonListContext()

  return (
    <CardListItem
      textValue={title}
      onAction={onAction}
      isDisabled={isDisabled}
      testId={testId}
    >
      <CardItemContent
        isInteractive={!isPending}
        icon={
          <CardItemIcon
            skeleton={isSkeleton}
            name={icon}
            variant="surface-background"
          />
        }
        extras={
          isPending && (
            <div className="pr-sm">
              <Spinner />
            </div>
          )
        }
      >
        <CardTitle>
          <AutoSkeleton isSkeleton={isSkeleton} characters={vary(15)}>
            {title}
          </AutoSkeleton>
        </CardTitle>
        <CardSubtitle variant="label">
          <AutoSkeleton isSkeleton={isSkeleton} characters={vary(80)}>
            {description}
          </AutoSkeleton>
        </CardSubtitle>
      </CardItemContent>
    </CardListItem>
  )
}

/**
 * The ServiceTypeSkeleton
 */
export function ServiceTypeSkeleton() {
  return (
    <ServiceTypeListItem
      title="title"
      description="description"
      icon="rocket"
      isSkeleton
    />
  )
}
